import { PureComponent, createRef, forwardRef } from 'react';
import Chart from './Chart';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import { TimeSeries } from 'd3visual';
import Wrapper from './Wrapper';
import { timeseries as copy } from '@/copy';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

const WIDTH = 1100;
const HEIGHT = 200;
const X_AXIS_HEIGHT = 35;
const Y_AXIS_WIDTH = 45;
const PADDING = 20;
const CIRCLE_RADIUS = 8;

const INIT = 'INIT';
const LOADING = 'LOADING';
const LOADED = 'LOADED';
const EMPTY = 'EMPTY';

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    width: 100%;
    min-height: 100%;
    position: absolute;
    top: 0;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    background: ${({ theme, status }) =>
        status === EMPTY ? 'transparent' : theme.neutral_6};
    display: flex;
    justify-content: center;
    align-items: center;
    justify-self: stretch;
    ${({ status }) => (status !== EMPTY ? PlaceholderShimmerBackground : null)}
`;

const getMessage = (status) => {
    switch (status) {
        case INIT:
            return copy.init;
        case LOADING:
            return copy.loading;
        case EMPTY:
        default:
            return copy.empty;
    }
};

function StatusMessage({ status = INIT }) {
    return (
        <div
            css={`
                position: relative;
                padding-top: ${HEIGHT}px;
                width: 100%;
            `}
        >
            <Placeholder status={status}>
                <span>{getMessage(status)}</span>
            </Placeholder>
        </div>
    );
}

const notEmpty = (trend) => {
    if (!trend) return false;
    const ts = Object.values(trend).shift();
    return !!(ts && ts.length);
};

const getNextStatus = (trendData, status) => {
    if (status) return status;
    if (!trendData) return INIT;
    if (trendData.length && trendData.some(notEmpty)) return LOADED;
    return EMPTY;
};

const calculatePredictionStart = (trendLen, predictionLen, width) => {
    if (!predictionLen) return `100%`;
    const lineWidth = width - Y_AXIS_WIDTH - PADDING - CIRCLE_RADIUS;
    const historicalPercentage = trendLen / (trendLen + predictionLen);
    const historicalPixelWidth = historicalPercentage * lineWidth;
    const predictionStartPercent =
        ((Y_AXIS_WIDTH + historicalPixelWidth) / width) * 100;
    return `${predictionStartPercent}%`;
};

const INIT_DATA = [
    {
        trend: [],
        prediction: [],
    },
];

class TimeSeriesChart extends PureComponent {
    state = {
        data: INIT_DATA,
        status: INIT,
        width: WIDTH,
        height: HEIGHT,
    };

    containerRef = createRef();

    componentDidMount() {
        const { trendData, status } = this.props;
        const nextStatus = getNextStatus(trendData, status);
        if (!!trendData && !!trendData.length) {
            this.setState(
                {
                    data: trendData.map((data) => this.formatTrendData(data)),
                    status: nextStatus,
                },
                () => {
                    this.renderVisualization();
                },
            );
        } else {
            this.setState({ status: nextStatus });
        }
    }

    componentDidUpdate(prevProps, { width: prevWidth, height: prevHeight }) {
        const { width, height } = this.state;
        const { trendData, status } = this.props;
        const nextStatus = getNextStatus(trendData, status);

        if (!isEqual(trendData, prevProps.trendData)) {
            this.setState(
                {
                    data: trendData
                        ? trendData.map((data) => this.formatTrendData(data))
                        : INIT_DATA,
                    status: nextStatus,
                },
                () => {
                    this.renderVisualization();
                },
            );
        } else if (prevProps.status !== status) {
            this.setState(
                { status: nextStatus },
                trendData ? this.renderVisualization : null,
            );
        } else if (prevWidth !== width || prevHeight !== height) {
            this.renderVisualization();
        }
    }

    formatTrendData = (trendData) => {
        const trend = [];
        const prediction = [];
        trendData = trendData ? Object.values(trendData).shift() : null;
        const header = trendData ? trendData[0] : null;
        trendData = trendData ? trendData.slice(1) : [];
        if (header)
            trendData.forEach((item) => {
                const volume = item[header.real_volume];
                const formatted = {
                    date: item[header.date],
                    value: volume,
                    upper: volume + 10,
                    lower: volume - 10,
                };

                // TODO: Get actual data for upper and lower!

                item[header.prediction]
                    ? prediction.push(formatted)
                    : trend.push(formatted);
            });
        return { trend, prediction };
    };

    renderVisualization = () => {
        const { data, width, height } = this.state;
        const { predictionMonths = 12 } = this.props;

        const visualization = new TimeSeries({
            container: this.containerRef.current,
            data,
            width,
            height,
            predictionMonths,
            xAxisHeight: X_AXIS_HEIGHT,
            yAxisWidth: Y_AXIS_WIDTH,
            padding: PADDING,
            circleRadius: CIRCLE_RADIUS,
        });
        visualization.init();
        visualization.render();
    };

    onResize = (width, height) => {
        if (width === this.state.width && height === this.state.height) return;
        this.setState({ width, height });
    };

    render() {
        const { status, data, width } = this.state;
        const { children, predictionMonths = 12 } = this.props;

        return (
            <div ref={this.props.wrapperRef}>
                <Wrapper
                    predictionStart={calculatePredictionStart(
                        data[0].trend.length || 36,
                        predictionMonths || data[0].prediction.length,
                        width,
                    )}
                >
                    {children}
                    <Chart onResize={this.onResize} height={HEIGHT}>
                        {status !== LOADED ? (
                            <StatusMessage status={status} />
                        ) : (
                            <div className="insight-details">
                                <div
                                    ref={this.containerRef}
                                    className="insight-detail insight-detail-2 data-vis"
                                ></div>
                            </div>
                        )}
                    </Chart>
                </Wrapper>
            </div>
        );
    }
}

const TimeSeriesChartWithRef = forwardRef((props, ref) => (
    <TimeSeriesChart wrapperRef={ref} {...props} />
));

export default TimeSeriesChartWithRef;
